import * as React from 'react'
import { companiesClient } from '@/lib/api'
import { partnerClient, type PartnerInfo } from '@/lib/api/partner.client'
import { ActivationStatus } from '@super/shared-types'
import type {
  CompanyResponse,
  UpdateCompanyDto,
  WorkOSUser,
} from '@super/shared-types'
import { toast } from 'sonner'
import { useSuperAdminCompany } from '@/hooks/use-super-admin-company'
import { useAuth } from '@/hooks/use-auth'
import { userContextApiClient } from '@/lib/api/user-context.client'

interface AppAuthContextValue {
  // User and company state
  user: WorkOSUser | null
  currentCompany: CompanyResponse | null
  currentCompanyPartner: PartnerInfo | null // Partner info when super admin views a partner company
  isSuperAdmin: boolean
  isPartner: boolean
  featureFlags: string[]
  activationStatus: ActivationStatus
  emailDomain: string | null // Inbound email domain (EMAIL_DOMAIN env)
  selectedCompanyId: string | null // For super admin company switching

  // Loading states
  isAuthLoading: boolean // From useAuth hook
  isAppContextLoading: boolean // When fetching user context + company data
  isCompanyLoading: boolean // When switching/updating company
  isInitialized: boolean // After first auth + company load

  // Actions
  signIn: (opts?: { state?: string; mode?: 'signin' | 'signup' }) => void
  signOut: () => void
  switchSuperAdminCompany: (
    companyId: string,
  ) => Promise<CompanyResponse | null>
  updateCompany: (
    companyId: string,
    data: UpdateCompanyDto,
  ) => Promise<CompanyResponse>
  refetchCurrentCompany: () => Promise<CompanyResponse | null>
  refetchAppContext: () => Promise<void>
  getAccessToken: () => Promise<string | null> // For WorkOS widgets only
}

const AppAuthContext = React.createContext<AppAuthContextValue | null>(null)

interface AppAuthProviderProps {
  children: React.ReactNode
}

export function AppAuthProvider({ children }: AppAuthProviderProps) {
  // Use custom auth hook with cookie-based backend session
  const {
    user,
    isSuperAdmin: currentIsSuperAdmin,
    isLoading: authLoading,
    signIn,
    signOut,
    getAccessToken,
  } = useAuth()

  // Use the super admin company hook
  const { selectedCompanyId, setSelectedCompanyId, clearSelectedCompany } =
    useSuperAdminCompany()

  const [currentCompany, setCurrentCompany] =
    React.useState<CompanyResponse | null>(null)
  const [currentCompanyPartner, setCurrentCompanyPartner] =
    React.useState<PartnerInfo | null>(null)
  const [featureFlags, setFeatureFlags] = React.useState<string[]>([])
  const [activationStatus, setActivationStatus] =
    React.useState<ActivationStatus>(ActivationStatus.PENDING)
  const [emailDomain, setEmailDomain] = React.useState<string | null>(null)
  const [isPartner, setIsPartner] = React.useState(false)
  const [isAppContextLoading, setIsAppContextLoading] = React.useState(false)
  const [isCompanyLoading, setIsCompanyLoading] = React.useState(false)
  const [isInitialized, setIsInitialized] = React.useState(false)

  // Ref to read selectedCompanyId without making it a dependency.
  // When user switches via switchSuperAdminCompany, we must NOT re-run fetchUserContext
  const selectedCompanyIdRef = React.useRef(selectedCompanyId)
  selectedCompanyIdRef.current = selectedCompanyId

  // Fetch user context and company data in parallel (initial load only).
  // Does NOT run when selectedCompanyId changes - switchSuperAdminCompany handles that.
  const fetchUserContext = React.useCallback(async () => {
    if (!user) {
      setCurrentCompany(null)
      setCurrentCompanyPartner(null)
      setFeatureFlags([])
      setEmailDomain(null)
      setIsPartner(false)
      return
    }

    setIsAppContextLoading(true)
    setIsCompanyLoading(true)

    try {
      const companyId = selectedCompanyIdRef.current

      // Determine which company to fetch
      const fetchCompany = async () => {
        if (currentIsSuperAdmin && companyId) {
          try {
            return await companiesClient.getCompanyById(companyId)
          } catch (error) {
            console.error('Error loading selected company for Admin:', error)
            clearSelectedCompany()
            return await companiesClient.getCurrentCompany()
          }
        }
        return await companiesClient.getCurrentCompany()
      }

      // Fetch user context and company in parallel
      const [userContext, company] = await Promise.all([
        userContextApiClient.getUserContext(),
        fetchCompany(),
      ])

      setFeatureFlags(userContext?.featureFlags || [])
      setActivationStatus(
        userContext?.activationStatus ?? ActivationStatus.PENDING,
      )
      setEmailDomain(userContext?.emailDomain ?? null)
      setIsPartner(userContext?.isPartner || false)
      setCurrentCompany(company)

      // If super admin is viewing a partner company, fetch partner info
      if (
        currentIsSuperAdmin &&
        company.isPartner &&
        company.provisionedPartnerId
      ) {
        try {
          const partner = await partnerClient.getPartnerById(
            company.provisionedPartnerId,
          )
          setCurrentCompanyPartner(partner)
        } catch (error) {
          console.error('Error fetching partner info:', error)
          setCurrentCompanyPartner(null)
        }
      } else {
        setCurrentCompanyPartner(null)
      }
    } catch (error) {
      console.error('Error fetching user context:', error)
      setCurrentCompany(null)
      setCurrentCompanyPartner(null)
      setFeatureFlags([])
      setEmailDomain(null)
      setIsPartner(false)
      toast.error('Failed to load user information')
    } finally {
      setIsAppContextLoading(false)
      setIsCompanyLoading(false)
    }
  }, [user, currentIsSuperAdmin, clearSelectedCompany])

  // Initialize auth state when user is available
  React.useEffect(() => {
    if (!authLoading) {
      if (user) {
        fetchUserContext()
      } else {
        setCurrentCompany(null)
        setCurrentCompanyPartner(null)
        setFeatureFlags([])
        setEmailDomain(null)
        setIsPartner(false)
        clearSelectedCompany()
      }
      setIsInitialized(true)
    }
  }, [user, authLoading, fetchUserContext, clearSelectedCompany])

  const handleSignOut = React.useCallback(() => {
    try {
      // Clear local state before redirect
      setCurrentCompany(null)
      setCurrentCompanyPartner(null)
      clearSelectedCompany()

      // Redirect to backend logout (this will redirect to WorkOS logout)
      signOut()
    } catch (error) {
      console.error('Error signing out:', error)
      toast.error('Failed to sign out')
    }
  }, [signOut, clearSelectedCompany])

  const switchSuperAdminCompany = React.useCallback(
    async (companyId: string): Promise<CompanyResponse | null> => {
      if (!currentIsSuperAdmin) {
        toast.error('Only Super Admins can switch companies')
        return null
      }

      setIsCompanyLoading(true)
      try {
        // Fetch the full company data by ID
        const company = await companiesClient.getCompanyById(companyId)

        // Only update company and selected ID after successful fetch
        setCurrentCompany(company)
        setSelectedCompanyId(companyId)

        // If this is a partner company, fetch partner info
        if (company.isPartner && company.provisionedPartnerId) {
          try {
            const partner = await partnerClient.getPartnerById(
              company.provisionedPartnerId,
            )
            setCurrentCompanyPartner(partner)
          } catch (error) {
            console.error('Error fetching partner info:', error)
            setCurrentCompanyPartner(null)
          }
        } else {
          setCurrentCompanyPartner(null)
        }

        toast.success(`Switched to ${company.name}`, {
          description: 'Viewing as Super Data Admin',
        })

        return company
      } catch (error) {
        console.error('Error switching company:', error)
        toast.error('Failed to switch company')
        return null
      } finally {
        setIsCompanyLoading(false)
      }
    },
    [currentIsSuperAdmin, setSelectedCompanyId],
  )

  const updateCompany = React.useCallback(
    async (companyId: string, data: UpdateCompanyDto) => {
      try {
        // Use admin endpoint if super admin is viewing a different company
        const updatedCompany =
          currentIsSuperAdmin && selectedCompanyId
            ? await companiesClient.updateCompanyAsAdmin(companyId, data)
            : await companiesClient.updateCompany(companyId, data)

        // Update local state if this is the current company
        if (currentCompany?.id === companyId) {
          setCurrentCompany(updatedCompany)
        }
        return updatedCompany
      } catch (error) {
        console.error('Error updating company:', error)
        throw error
      }
    },
    [currentCompany?.id, currentIsSuperAdmin, selectedCompanyId],
  )

  const refetchCurrentCompany = React.useCallback(async () => {
    if (!currentCompany?.id) return null
    try {
      const fresh =
        currentIsSuperAdmin && selectedCompanyId
          ? await companiesClient.getCompanyById(currentCompany.id)
          : await companiesClient.getCurrentCompany()
      setCurrentCompany(fresh)
      return fresh
    } catch (error) {
      console.error('Error refetching company:', error)
      return null
    }
  }, [currentCompany?.id, currentIsSuperAdmin, selectedCompanyId])

  const value: AppAuthContextValue = React.useMemo(
    () => ({
      // State
      user,
      currentCompany,
      currentCompanyPartner,
      isSuperAdmin: currentIsSuperAdmin,
      isPartner,
      featureFlags,
      activationStatus,
      emailDomain,
      selectedCompanyId,
      isAuthLoading: authLoading,
      isAppContextLoading,
      isCompanyLoading,
      isInitialized,

      // Actions
      signIn,
      signOut: handleSignOut,
      switchSuperAdminCompany,
      updateCompany,
      refetchCurrentCompany,
      refetchAppContext: fetchUserContext,
      getAccessToken,
    }),
    [
      user,
      currentCompany,
      currentCompanyPartner,
      currentIsSuperAdmin,
      isPartner,
      featureFlags,
      activationStatus,
      emailDomain,
      selectedCompanyId,
      authLoading,
      isAppContextLoading,
      isCompanyLoading,
      isInitialized,
      signIn,
      handleSignOut,
      switchSuperAdminCompany,
      updateCompany,
      refetchCurrentCompany,
      fetchUserContext,
      getAccessToken,
    ],
  )

  return (
    <AppAuthContext.Provider value={value}>{children}</AppAuthContext.Provider>
  )
}

export function useAppAuth(): AppAuthContextValue {
  const context = React.useContext(AppAuthContext)

  if (!context) {
    throw new Error('useAppAuth must be used within an AppAuthProvider')
  }

  return context
}
